import React, { ReactElement, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import SellerReviewCard from '../card/seller-review-card';
import { SELLER_REVIEWS_SHOW_SUFFIX } from '../../../utils/constants';
import { PaginatedResponse } from '../../../structures/requests/paginatedResponses';
import HTMLReactParserSafe from '../../partials/html-react-parser-safe';

interface SellerReviewsProps {
  sellerId: string
}

export interface Review {
  id: number;
  authorName: string;
  seller_id: number;
  message: string;
  rating: number;
  created_at: string;
  updated_at: string;
}

interface SellerReviewsResponse extends PaginatedResponse {
  data: Review[]
}

const SellerReviews: React.FC<SellerReviewsProps> = ({ sellerId }): ReactElement => {
  const [reviews, setReviews] = useState<SellerReviewsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadReviews = (page: string | null) => {
    axios.get(`${page ?? SELLER_REVIEWS_SHOW_SUFFIX(sellerId)}`)
      .then(({ data }) => {
        setReviews(data);
        setError(null);
      })
      .catch(() => {
        setReviews(null);
        setError('An error occurred while fetching reviews');
      });
  };

  useEffect(() => {
    loadReviews(null);
  }, []);

  if (error) {
    return <div>{error}</div>;
  }

  if (!reviews) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="review-cards-container">
        {
          reviews.data.map((review) => (
            <SellerReviewCard
              review={review}
              canUpdateStatus={false}
              isAdmin={false}
              key={review.id}
            />
          ))
        }
      </div>
      {
        reviews.meta.last_page > 1 && (
          <div className="review-cards-pagination">
            <nav>
              <ul className="pagination">
                {
                  reviews.meta.links.map((link) => {
                    const isDisabled = link.label === '...' || link.active || !link.url;
                    return (
                      <li
                        key={link.label + link.url}
                        className={`page-item ${link.active ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                      >
                        <a
                          className={`page-link ${link.active ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            loadReviews(link.url ?? '');
                            const reviewsTop = document.getElementById('seller-reviews');
                            if (reviewsTop) {
                              reviewsTop.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }}
                        >
                          <HTMLReactParserSafe html={link.label} />
                        </a>
                      </li>
                    );
                  })
                }
              </ul>
            </nav>
          </div>
        )
      }
    </>
  );
};

if (document.getElementsByClassName('react-seller-review-card-list').length > 0) {
  document.querySelectorAll('.react-seller-review-card-list').forEach((element) => {
    const sellerId = element.getAttribute('data-seller-id') as string;
    ReactDOM.render(
      <SellerReviews sellerId={sellerId} />,
      element,
    );
  });
}
